import type {ChangeEvent, FocusEvent, KeyboardEvent} from 'react';
import {useCallback, useEffect, useState} from 'react';
import type {
  NativeSyntheticEvent,
  TextInputChangeEventData,
  TextInputFocusEventData,
  TextInputKeyPressEventData,
} from 'react-native';

import type {ChangeEventPayload, KeyPressEventPayload, Props} from './types';

import {createEventNormalizer} from '../../utils/events';
import {isDefined} from '../../utils/typecheck';
import {ClearButton, ClearIcon, ErrorText, SearchIcon} from './styles';

export const useController = ({
  appearance,
  defaultValue,
  disabled,
  error,
  errorStyle,
  isClearable,
  onBlur,
  onChange,
  onClear,
  onFocus,
  onKeyPress,
  value: valueProp,
}: Props) => {
  const [value, setValue] = useState(valueProp || defaultValue || '');

  /**
   * Keep controlled inputs in sync without scheduling an update after every
   * render. The value dependency preserves the old "reset to the prop on any
   * local drift" behavior while the equality guard avoids render feedback loops.
   */
  useEffect(() => {
    if (!isDefined(valueProp)) {
      return;
    }

    const nextValue = valueProp || '';

    if (value !== nextValue) {
      setValue(nextValue);
    }
  }, [value, valueProp]);

  const shouldShowClearButton = isClearable && value;

  const changeHandler = useCallback(
    event => {
      const normalized = changeEventNormalizer(event);

      onChange?.(normalized);

      setValue(normalized.payload?.text || '');
    },
    [onChange],
  );

  const focusHandler = useCallback(
    event => {
      const normalized = focusEventNormalizer(event);

      onFocus?.(normalized);
    },
    [onFocus],
  );

  const blurHandler = useCallback(
    event => {
      const normalized = focusEventNormalizer(event);

      onBlur?.(normalized);
    },
    [onBlur],
  );

  const keyPressHandler = useCallback(
    event => {
      const normalized = keyPressEventNormalizer(event);

      onKeyPress?.(normalized);
    },
    [onKeyPress],
  );

  const clearHandler = useCallback(() => {
    setValue('');

    onClear?.();
  }, [onClear]);

  const renderSearchIcon = () =>
    appearance === 'search' ? <SearchIcon /> : null;

  const renderError = () =>
    typeof error === 'string' ? (
      <ErrorText
        disabled={disabled}
        nativeProps={{numberOfLines: 1}}
        style={errorStyle}
      >
        {error}
      </ErrorText>
    ) : null;

  const renderClearButton = () =>
    shouldShowClearButton ? (
      <ClearButton onClick={clearHandler}>
        <ClearIcon />
      </ClearButton>
    ) : null;

  return {
    blurHandler,
    changeHandler,
    focusHandler,
    keyPressHandler,
    renderClearButton,
    renderError,
    renderSearchIcon,
    value,
  };
};

const changeEventNormalizer = createEventNormalizer<
  ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  NativeSyntheticEvent<TextInputChangeEventData>,
  ChangeEventPayload
>(
  event => ({
    text: event.currentTarget.value,
  }),
  event => ({
    text: event.nativeEvent.text,
  }),
);

const focusEventNormalizer = createEventNormalizer<
  FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  NativeSyntheticEvent<TextInputFocusEventData>,
  null
>();

const keyPressEventNormalizer = createEventNormalizer<
  KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  NativeSyntheticEvent<TextInputKeyPressEventData>,
  KeyPressEventPayload
>(
  event => ({
    key: event.key,
  }),
  event => ({
    key: event.nativeEvent.key,
  }),
);
